import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const PENDING = new Set(["queued", "processing"]);
const feedCache = new Map(); // cacheKey -> snapshot, so Library survives switching tabs
let lastScrollY = 0;
if (typeof window !== "undefined") window.addEventListener("scroll", () => { lastScrollY = window.scrollY; }, { passive: true });

const appendUnique = (prev, next) => {
  const seen = new Set(prev.map((i) => i.id));
  return [...prev, ...next.filter((i) => !seen.has(i.id))];
};

export function useFeed(fetchPage, { poll = true, cacheKey = null } = {}) {
  const [cached] = useState(() => (cacheKey ? feedCache.get(cacheKey) : null));
  const [items, setItems] = useState(cached?.items || []);
  const [hasMore, setHasMore] = useState(cached ? cached.hasMore : true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(cached?.total ?? null);
  const [meta, setMeta] = useState(cached?.meta || null);
  const cursor = useRef(cached?.cursor || null);
  const restoredFor = useRef(null); // reload fn the cached snapshot belongs to (survives StrictMode re-runs)
  const snap = useRef(null);
  snap.current = { items, hasMore, total, meta };
  const req = useRef(0);
  const busy = useRef(false);
  const retryAt = useRef(0);

  const load = useCallback(async (reset) => {
    const id = ++req.current;
    busy.current = true;
    setLoading(true);
    try {
      const data = await fetchPage(reset ? null : cursor.current);
      if (id !== req.current) return;
      setItems((prev) => (reset ? data.items : appendUnique(prev, data.items)));
      cursor.current = data.next_cursor;
      setHasMore(Boolean(data.next_cursor));
      if (data.total != null) setTotal(data.total);
      if (reset) setMeta(data);
    } catch (e) {
      // back off (e.g. 429 from the edge) instead of hammering the API from the scroll trigger
      retryAt.current = Date.now() + 4000;
      if (id === req.current) setTimeout(() => setLoading(false), 0);
    } finally {
      if (id === req.current) {
        busy.current = false;
        setLoading(false);
      }
    }
  }, [fetchPage]);

  const reload = useCallback(() => {
    cursor.current = null;
    return load(true);
  }, [load]);

  useEffect(() => {
    if (cached && (restoredFor.current === null || restoredFor.current === reload)) {
      restoredFor.current = reload;
      return;
    }
    setItems([]);
    setHasMore(true);
    reload();
  }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (cacheKey) feedCache.set(cacheKey, { ...snap.current, cursor: cursor.current, scrollY: lastScrollY });
  }, [cacheKey]);

  const loadMore = useCallback(() => {
    if (!busy.current && cursor.current && Date.now() >= retryAt.current) load(false);
  }, [load]);

  const pendingIds = items.filter((i) => PENDING.has(i.status)).map((i) => i.id).join(",");
  useEffect(() => {
    if (!poll || !pendingIds) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.post("/uploads/status", { item_ids: pendingIds.split(",").slice(0, 500) }).catch(() => ({ data: { items: [] } }));
      const finished = data.items.filter((s) => !PENDING.has(s.status));
      if (!finished.length) return;
      const fresh = await Promise.all(finished.filter((s) => !s.duplicate).map((s) => api.get(`/items/${s.id}`).then((r) => r.data)));
      const byId = Object.fromEntries(fresh.map((f) => [f.id, f]));
      const drop = new Set(finished.filter((s) => s.duplicate).map((s) => s.id));
      setItems((prev) => prev.filter((i) => !drop.has(i.id)).map((i) => byId[i.id] || i));
    }, 4000);
    return () => clearInterval(t);
  }, [pendingIds, poll]);

  const removeIds = useCallback((ids) => {
    const s = new Set(ids);
    setItems((prev) => prev.filter((i) => !s.has(i.id)));
  }, []);

  return { items, setItems, hasMore, loading, total, meta, loadMore, reload, removeIds, restoredScrollY: cached?.scrollY ?? null };
}
